// src/pages/Billing.jsx - FIXED VERSION with proper usage stats display

import React, { useState } from 'react';
import {useQuery} from 'react-query';
import {
    CheckIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    XMarkIcon,
    ArrowDownIcon,
    StopIcon,
    ArrowPathIcon,
    CreditCardIcon,
    ChartBarIcon,
    ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useBilling, useSubscriptionStatus } from '../hooks/useBilling';
import {billingAPI} from "../services/api.js";
import { toast } from 'react-hot-toast';

// Enhanced Confirmation Modal Component with no-refunds policy
const ConfirmationModal = ({
                               isOpen,
                               onClose,
                               title,
                               children,
                               onConfirm,
                               confirmText = 'Confirm',
                               confirmClass = 'bg-blue-600 hover:bg-blue-700',
                               isLoading = false,
                               showNoRefundsWarning = false,
                               hideDefaultButtons = false // ADD this prop
                           }) => {
    const [confirmedNoRefunds, setConfirmedNoRefunds] = useState(false);

    if (!isOpen) return null;

    const canConfirm = !showNoRefundsWarning || confirmedNoRefunds;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
                    <div className="mb-4">
                        {typeof children === 'function' ? children({ confirmedNoRefunds }) : children}
                    </div>


                    {/* No Refunds Warning */}
                    {showNoRefundsWarning && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                            <div className="flex">
                                <ExclamationCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">
                                        No Refunds Policy
                                    </h3>
                                    <div className="mt-2 text-sm text-red-700">
                                        <p>
                                            <strong>Important:</strong> No refunds will be issued for canceled subscriptions.
                                            You will retain access to your current plan until the end of your billing period.
                                        </p>
                                    </div>
                                    <div className="mt-3">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={confirmedNoRefunds}
                                                onChange={(e) => setConfirmedNoRefunds(e.target.checked)}
                                                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                            />
                                            <span className="ml-2 text-sm text-red-700">
                                                I understand that no refunds will be issued
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Only show default buttons if not explicitly hidden and onConfirm exists */}
                    {!hideDefaultButtons && onConfirm && confirmText && (
                        <div className="flex gap-3">
                            <button
                                onClick={onConfirm}
                                disabled={isLoading || !canConfirm}
                                className={`flex-1 text-white py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${confirmClass}`}
                            >
                                {isLoading ? 'Processing...' : confirmText}
                            </button>
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Status Banner Component
const StatusBanner = ({ type, title, children, onClose, icon: Icon, bgColor, textColor, borderColor, purchasesEnabled }) => (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
        <div className="flex items-start">
            <Icon className={`h-6 w-6 ${textColor} mr-3 mt-0.5`} />
            <div className="flex-1">
                <h3 className={`text-lg font-medium ${textColor.replace('400', '800')}`}>{title}</h3>
                <div className={`mt-2 text-sm ${textColor.replace('400', '700')}`}>
                    {children}
                </div>
            </div>
            <button
                onClick={onClose}
                className={`${textColor} hover:${textColor.replace('400', '600')}`}
            >
                <XMarkIcon className="h-5 w-5" />
            </button>
        </div>


    </div>

);

// ADDED: Usage Stats Card Component
const UsageStatsCard = ({ usageStats, quotaInfo, currentPlan, isLoading }) => {
    if (isLoading) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-300 rounded w-1/4 mb-4"></div>
                    <div className="h-8 bg-gray-300 rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                </div>
            </div>
        );
    }



    const generated = usageStats?.generated || 0;
    const limit = usageStats?.limit || quotaInfo?.limits?.postsPerMonth || 10;
    const remaining = usageStats?.remaining || (limit - generated);
    const usagePercent = usageStats?.usagePercent || Math.round((generated / limit) * 100);
    const lastReset = usageStats?.lastResetDate || quotaInfo?.usage?.lastResetDate;

    // Determine progress bar color based on usage
    const getProgressColor = () => {
        if (usagePercent >= 90) return 'bg-red-500';
        if (usagePercent >= 75) return 'bg-yellow-500';
        if (usagePercent >= 50) return 'bg-blue-500';
        return 'bg-green-500';
    };

    const getTextColor = () => {
        if (usagePercent >= 90) return 'text-red-700';
        if (usagePercent >= 75) return 'text-yellow-700';
        return 'text-gray-700';
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <ChartBarIcon className="h-5 w-5 mr-2 text-gray-400" />
                    Monthly Usage
                </h3>
                <span className="text-sm text-gray-500 capitalize">{currentPlan} Plan</span>
            </div>

            <div className="space-y-4">
                {/* Usage Progress Bar */}
                <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Posts Generated</span>
                        <span className={getTextColor()}>
                            {generated} of {limit} ({usagePercent}%)
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                            className={`h-3 rounded-full transition-all duration-300 ${getProgressColor()}`}
                            style={{ width: `${Math.min(usagePercent, 100)}%` }}
                        ></div>
                    </div>
                </div>

                {/* Usage Stats Grid */}
                <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-blue-50 rounded-lg p-3">
                        <div className="text-2xl font-bold text-blue-600">{generated}</div>
                        <div className="text-xs text-blue-700">Generated</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                        <div className="text-2xl font-bold text-green-600">{remaining}</div>
                        <div className="text-xs text-green-700">Remaining</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                        <div className="text-2xl font-bold text-purple-600">{usageStats?.published || 0}</div>
                        <div className="text-xs text-purple-700">Published</div>
                    </div>
                </div>

                {/* Quota Warning */}
                {usagePercent >= 80 && (
                    <div className={`p-3 rounded-md ${usagePercent >= 95 ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                        <div className="flex">
                            <ExclamationTriangleIcon className={`h-5 w-5 ${usagePercent >= 95 ? 'text-red-400' : 'text-yellow-400'} flex-shrink-0`} />
                            <div className="ml-3">
                                <h3 className={`text-sm font-medium ${usagePercent >= 95 ? 'text-red-800' : 'text-yellow-800'}`}>
                                    {usagePercent >= 95 ? 'Quota Almost Exceeded' : 'High Usage Warning'}
                                </h3>
                                <div className={`mt-1 text-sm ${usagePercent >= 95 ? 'text-red-700' : 'text-yellow-700'}`}>
                                    <p>
                                        You've used {usagePercent}% of your monthly quota.
                                        {currentPlan === 'free' && (
                                            <span> Consider upgrading for more posts.</span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Reset Date */}
                {lastReset && (
                    <div className="text-xs text-gray-500 text-center">
                        Quota resets monthly • Last reset: {new Date(lastReset).toLocaleDateString()}
                    </div>
                )}
            </div>
        </div>
    );
};

// Add this new component after UsageStatsCard
const BillingHistorySection = ({ invoices, isLoading, onRefresh }) => {
    const handleDownload = (invoice) => {
        if (invoice.pdfUrl) {
            window.open(invoice.pdfUrl, '_blank');
        } else {
            toast.error('Invoice PDF not available');
        }
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatAmount = (amount, currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase()
        }).format(amount / 100);
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            paid: 'bg-green-100 text-green-800',
            open: 'bg-yellow-100 text-yellow-800',
            void: 'bg-gray-100 text-gray-800',
            uncollectible: 'bg-red-100 text-red-800'
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    if (isLoading) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                    <div className="space-y-2">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-12 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Billing History</h3>
                <button
                    onClick={onRefresh}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                    <ArrowPathIcon className="h-4 w-4 mr-1" />
                    Refresh
                </button>
            </div>

            {invoices.length === 0 ? (
                <div className="text-center py-8">
                    <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No invoices found</p>
                </div>
            ) : (
                <div className="overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {invoices.map((invoice) => (
                            <tr key={invoice.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatDate(invoice.date)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {invoice.description}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                    {formatAmount(invoice.amount, invoice.currency)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getStatusBadge(invoice.status)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {invoice.pdfUrl ? (
                                        <button
                                            onClick={() => handleDownload(invoice)}
                                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            <ArrowDownIcon className="h-4 w-4 mr-1" />
                                            Download
                                        </button>
                                    ) : (
                                        <span className="text-gray-400 text-sm">Not available</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// Plan Card Component
const PlanCard = ({
                      plan,
                      isCurrentPlan,
                      canUpgrade,
                      canDowngrade,
                      onUpgrade,
                      onDowngrade,
                      isLoading,
                      purchasesEnabled
                  }) => (
    <div
        className={`relative rounded-lg border-2 p-6 ${
            isCurrentPlan
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white'
        }`}
    >
        {isCurrentPlan && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-3 py-1 text-xs font-medium rounded-full">
                    Current Plan
                </span>
            </div>
        )}

        <div className="text-center">
            <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
            <div className="mt-2">
                <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                <span className="text-sm text-gray-600">/month</span>
            </div>
            <p className="mt-2 text-sm text-gray-600">{plan.description}</p>
        </div>

        <ul className="mt-6 space-y-3">
            {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                    <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="ml-3 text-sm text-gray-700">{feature}</span>
                </li>
            ))}
        </ul>

        <div className="mt-8">
            {isCurrentPlan ? (
                <button
                    disabled
                    className="w-full bg-gray-100 text-gray-400 py-2 px-4 rounded-md cursor-not-allowed"
                >
                    Current Plan
                </button>
            ) : canUpgrade ? (
                <button
                    onClick={() => onUpgrade(plan.priceId)}
                    disabled={!purchasesEnabled || isLoading}
                    className={`w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 ${
                        !purchasesEnabled
                            ? ' cursor-not-allowed'
                            : ''
                    }` }
                >
                    {isLoading ? 'Processing...' : 'Upgrade'}
                </button>
            ) : canDowngrade ? (
                <button
                    onClick={() => onDowngrade(plan.name.toLowerCase())}
                    disabled={isLoading}
                    className={`w-full bg-gray-100 text-gray-600 py-2 px-4 rounded-md font-medium hover:bg-gray-200 disabled:opacity-50 ${
                        !purchasesEnabled
                            ? 'bg-gray-100 text-gray-400'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                    Downgrade
                </button>
            ) : (
                <button
                    disabled
                    className="w-full bg-gray-100 text-gray-400 py-2 px-4 rounded-md cursor-not-allowed"
                >
                    Not Available
                </button>
            )}
        </div>

        {/*/!* Debug info in development *!/*/}
        {/*{process.env.NODE_ENV === 'development' && plan.priceId && (*/}

        {/*    <div className="mt-2 text-xs text-gray-500 font-mono break-all">*/}
        {/*        <div>Price ID: {plan.priceId}</div>*/}
        {/*        <div>Can upgrade: {canUpgrade ? 'Yes' : 'No'}</div>*/}
        {/*        <div>Can downgrade: {canDowngrade ? 'Yes' : 'No'}</div>*/}
        {/*    </div>*/}
        {/*)}*/}
    </div>
);

function Billing() {
    const billing = useBilling();
    const subscriptionStatus = useSubscriptionStatus();

    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedDowngradePlan, setSelectedDowngradePlan] = useState(null);
    const [billingHistory, setBillingHistory] = useState([]);
    const [billingHistoryLoading, setBillingHistoryLoading] = useState(false);
    const [showBillingHistory, setShowBillingHistory] = useState(false);


    const { data: billingHistoryData, refetch: refetchBillingHistory } = useQuery(
        'billingHistory',
        () => billingAPI.getBillingHistory({ limit: 20 }),
        {
            enabled: false, // Only fetch when user opens the section
            onSuccess: (response) => {
                setBillingHistory(response.data?.data?.invoices || []);
            },
            onError: (error) => {
                console.error('Failed to fetch billing history:', error);
            }
        }
    );



    const {
        subscription,
        plans,
        currentPlan,
        user,
        purchasesEnabled,
        sessionDetails,
        usageStats, // ADDED
        quotaInfo,  // ADDED
        isLoading,
        isProcessing,
        usageStatsLoading, // ADDED
        stripeConfigured,
        showSuccess,
        showCanceled,
        showUpdated,
        showReactivated,
        handleUpgrade,
        handleDowngrade,
        handleCancel,
        handleReactivate,
        manualRefresh,
        checkoutMutation,
        downgradeMutation,
        cancelMutation,
        reactivateMutation,
        canUpgradeTo,
        canDowngradeTo,
        setShowSuccess,
        setShowCanceled,
        setShowUpdated,
        setShowReactivated
    } = billing;

    // console.log('🔍 Billing Component Debug with Usage:', {
    //     subscription,
    //     subscriptionStatus,
    //     currentPlan,
    //     user: user ? {
    //         plan: user.plan,
    //         entitlement: user.entitlement,
    //         usageStats: user.usageStats
    //     } : null,
    //     usageStats,
    //     quotaInfo,
    //     usageStatsLoading
    // });

    const confirmDowngrade = () => {
        if (selectedDowngradePlan) {
            handleDowngrade(selectedDowngradePlan);
            setShowDowngradeModal(false);
        }
    };

    const confirmCancel = (immediate = false) => {
        handleCancel({ immediate, reason: 'user_requested' });
        setShowCancelModal(false);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    // FIXED: Determine effective plan and subscription info
    const effectivePlan = user?.entitlement || user?.plan || currentPlan || 'free';
    const hasActiveSubscription = subscription?.subscriptionId && subscription?.status === 'active';
    const hasAnySubscription = Boolean(subscription?.subscriptionId);

    return (
        <div className="space-y-6">
            {/* Status Banners */}
            {showSuccess && (
                <StatusBanner
                    type="success"
                    title="Payment Successful! 🎉"
                    onClose={() => setShowSuccess(false)}
                    icon={CheckCircleIcon}
                    bgColor="bg-green-50"
                    textColor="text-green-400"
                    borderColor="border-green-200"
                >
                    <p className="mb-2">
                        Your subscription has been processed successfully. Your account will be upgraded shortly.
                    </p>
                    {sessionDetails && (
                        <div className="bg-green-100 rounded p-3 mt-3">
                            <h4 className="font-medium text-green-800 mb-1">Transaction Details:</h4>
                            <ul className="text-xs space-y-1">
                                <li><strong>Amount:</strong> ${(sessionDetails.amount_total / 100).toFixed(2)} {sessionDetails.currency?.toUpperCase()}</li>
                                <li><strong>Payment Status:</strong> {sessionDetails.payment_status}</li>
                                <li><strong>Plan:</strong> {sessionDetails.plan}</li>
                            </ul>
                        </div>
                    )}
                </StatusBanner>
            )}

            {showReactivated && (
                <StatusBanner
                    type="reactivated"
                    title="Subscription Reactivated! 🔄"
                    onClose={() => setShowReactivated(false)}
                    icon={CheckCircleIcon}
                    bgColor="bg-green-50"
                    textColor="text-green-400"
                    borderColor="border-green-200"
                >
                    <p className="mb-2">
                        <strong>Great news!</strong> Your existing subscription has been reactivated at no additional charge.
                    </p>
                    <div className="bg-green-100 rounded p-3 mt-3">
                        <h4 className="font-medium text-green-800 mb-1">What happened:</h4>
                        <ul className="text-xs space-y-1 text-green-700">
                            <li>• You had an existing subscription that was scheduled for cancellation</li>
                            <li>• We've removed the cancellation and reactivated your access</li>
                            <li>• No new charges were processed</li>
                            <li>• Your billing cycle continues as normal</li>
                        </ul>
                    </div>
                </StatusBanner>
            )}

            {showUpdated && (
                <StatusBanner
                    type="updated"
                    title="Subscription Updated! 🎯"
                    onClose={() => setShowUpdated(false)}
                    icon={CheckCircleIcon}
                    bgColor="bg-blue-50"
                    textColor="text-blue-400"
                    borderColor="border-blue-200"
                >
                    <p>Your subscription has been successfully updated without prorated billing.</p>
                </StatusBanner>
            )}

            {showCanceled && (
                <StatusBanner
                    type="canceled"
                    title="Payment Canceled"
                    onClose={() => setShowCanceled(false)}
                    icon={ExclamationTriangleIcon}
                    bgColor="bg-yellow-50"
                    textColor="text-yellow-400"
                    borderColor="border-yellow-200"
                >
                    <p>
                        No worries! Your payment was canceled and no charges were made.
                        You can try again whenever you're ready to upgrade.
                    </p>
                </StatusBanner>
            )}

            {/* Page Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
                <p className="mt-2 text-sm text-gray-700">
                    Manage your subscription and billing information
                </p>
            </div>
            {!purchasesEnabled && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">Purchases Temporarily Disabled</h3>
                            <div className="mt-2 text-sm text-yellow-700">
                                <p>New license purchases are temporarily disabled. Existing subscriptions continue to work normally.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* Configuration Warning */}
            {!stripeConfigured && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">Billing Setup Required</h3>
                            <div className="mt-2 text-sm text-yellow-700">
                                <p>Stripe billing is not fully configured. Please complete the setup.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ADDED: Usage Stats Card */}
            <UsageStatsCard
                usageStats={usageStats}
                quotaInfo={quotaInfo}
                currentPlan={effectivePlan}
                isLoading={usageStatsLoading}
            />

            {/* FIXED: Current Plan Status */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Current Plan</h3>
                    <div className="flex items-center gap-2">
                        <ChartBarIcon className="h-5 w-5 text-gray-400" />
                        <span className={`text-sm font-medium ${subscriptionStatus.statusColor}`}>
                            {subscriptionStatus.statusText}
                        </span>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-lg font-semibold text-gray-900 capitalize">
                            {effectivePlan} Plan
                        </p>
                        <p className="text-sm text-gray-600">
                            {hasActiveSubscription
                                ? 'Active subscription'
                                : hasAnySubscription
                                    ? `Subscription ${subscription?.status || 'inactive'}`
                                    : 'No active subscription'
                            }
                            {subscriptionStatus.willCancel && (
                                <span className="text-amber-600 ml-2">• Canceling at period end</span>
                            )}
                        </p>

                        {/*/!* Debug info in development *!/*/}
                        {/*{process.env.NODE_ENV === 'development' && (*/}
                        {/*    <div className="mt-2 text-xs text-gray-500 font-mono">*/}
                        {/*        <div>User plan: {user?.plan || 'null'}</div>*/}
                        {/*        <div>User entitlement: {user?.entitlement || 'null'}</div>*/}
                        {/*        <div>Subscription ID: {subscription?.subscriptionId || 'none'}</div>*/}
                        {/*        <div>Subscription status: {subscription?.status || 'none'}</div>*/}
                        {/*        <div>Cancel at period end: {subscription?.cancelAtPeriodEnd ? 'Yes' : 'No'}</div>*/}
                        {/*    </div>*/}
                        {/*)}*/}
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-600">
                            Usage: {usageStats?.generated || 0} posts this month
                        </p>
                        {subscriptionStatus.renewalDate && (
                            <p className="text-sm text-gray-600">
                                {subscriptionStatus.willCancel ? 'Ends' : 'Renews'}: {subscriptionStatus.renewalDate}
                            </p>
                        )}
                    </div>
                </div>

                {/* FIXED: Subscription Actions */}
                {(hasAnySubscription || user?.customerId || effectivePlan !== 'free') && (
                    <div className="mt-4 flex gap-3 flex-wrap">
                        {subscriptionStatus.willCancel ? (
                            <button
                                onClick={handleReactivate}
                                disabled={reactivateMutation.isLoading}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <ArrowPathIcon className="h-4 w-4 mr-2" />
                                {reactivateMutation.isLoading ? 'Reactivating...' : 'Reactivate'}
                            </button>
                        ) : (
                            <>
                                {effectivePlan === 'enterprise' && (
                                    <button
                                        onClick={() => {
                                            setSelectedDowngradePlan('pro');
                                            setShowDowngradeModal(true);
                                        }}
                                        disabled={isProcessing}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <ArrowDownIcon className="h-4 w-4 mr-2" />
                                        Downgrade to Pro
                                    </button>
                                )}
                                {(effectivePlan === 'pro' || effectivePlan === 'enterprise') && (
                                    <button
                                        onClick={() => {
                                            setSelectedDowngradePlan('free');
                                            setShowDowngradeModal(true);
                                        }}
                                        disabled={isProcessing}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <ArrowDownIcon className="h-4 w-4 mr-2" />
                                        Downgrade to Free
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowCancelModal(true)}
                                    disabled={isProcessing}
                                    className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                                >
                                    <StopIcon className="h-4 w-4 mr-2" />
                                    Cancel Subscription
                                </button>
                            </>
                        )}

                        <button
                            onClick={manualRefresh}
                            disabled={isProcessing}
                            className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
                        >
                            <ArrowPathIcon className={`h-4 w-4 mr-2 ${isProcessing ? 'animate-spin' : ''}`} />
                            Refresh Status
                        </button>
                    </div>
                )}
            </div>

            {/* Available Plans */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6">Available Plans</h3>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {plans.map((plan) => {
                        const planName = plan.name.toLowerCase();
                        const isCurrentPlan = planName === effectivePlan;

                        return (
                            <PlanCard
                                key={plan.name}
                                plan={plan}
                                isCurrentPlan={isCurrentPlan}
                                canUpgrade={canUpgradeTo(planName)}
                                canDowngrade={canDowngradeTo(planName)}
                                onUpgrade={handleUpgrade}
                                onDowngrade={(targetPlan) => {
                                    setSelectedDowngradePlan(targetPlan);
                                    setShowDowngradeModal(true);
                                }}
                                isLoading={checkoutMutation.isLoading || isProcessing}
                            />
                        );
                    })}
                </div>
            </div>

            {/* Add this section after the Available Plans section */}
            {hasAnySubscription && (
                <>
                    {/* Billing History Toggle */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">Billing History</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    View and download your invoices
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowBillingHistory(!showBillingHistory);
                                    if (!showBillingHistory && billingHistory.length === 0) {
                                        refetchBillingHistory();
                                    }
                                }}
                                className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <CreditCardIcon className="h-4 w-4 mr-2" />
                                {showBillingHistory ? 'Hide' : 'Show'} Invoices
                            </button>
                        </div>
                    </div>

                    {/* Billing History Section */}
                    {showBillingHistory && (
                        <BillingHistorySection
                            invoices={billingHistory}
                            isLoading={billingHistoryLoading}
                            onRefresh={refetchBillingHistory}
                        />
                    )}
                </>
            )}

            {/* Downgrade Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDowngradeModal}
                onClose={() => setShowDowngradeModal(false)}
                title={`Confirm Downgrade to ${selectedDowngradePlan?.charAt(0).toUpperCase() + selectedDowngradePlan?.slice(1)}`}
                onConfirm={confirmDowngrade}
                confirmText="Confirm Downgrade"
                confirmClass="bg-orange-600 hover:bg-orange-700"
                isLoading={downgradeMutation.isLoading}
                showNoRefundsWarning={selectedDowngradePlan === 'free'}
            >
                <p className="text-sm text-gray-500">
                    {selectedDowngradePlan === 'free'
                        ? 'Your subscription will be canceled at the end of the current billing period.'
                        : 'Your subscription will be changed immediately with prorated billing.'}
                </p>
            </ConfirmationModal>

            {/* Enhanced Cancel Confirmation Modal */}
            {/* Enhanced Cancel Confirmation Modal */}
            <ConfirmationModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                title="Cancel Subscription"
                onConfirm={null} // CHANGE: Set to null instead of empty function
                confirmText={null} // CHANGE: Set to null instead of empty string
                isLoading={cancelMutation.isLoading}
                showNoRefundsWarning={true}
                hideDefaultButtons={true} // ADD: Explicitly hide default buttons
            >
                {({ confirmedNoRefunds }) => (
                    <div className="space-y-4">
                        <p className="text-sm text-gray-500">
                            Choose when you'd like your subscription to end:
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => confirmCancel(true)}
                                disabled={cancelMutation.isLoading || !confirmedNoRefunds}
                                className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                                Cancel Immediately
                            </button>
                            <button
                                onClick={() => setShowCancelModal(false)}
                                disabled={cancelMutation.isLoading}
                                className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 disabled:opacity-50"
                            >
                                Keep Subscription
                            </button>
                        </div>
                    </div>
                )}
            </ConfirmationModal>

            {/* General Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <CreditCardIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="space-y-4">
                        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <CreditCardIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-gray-800">Billing Information</h3>
                                    <div className="mt-2 text-sm text-gray-700">
                                        <p>
                                            Billing is handled securely through Stripe. You can cancel or modify your subscription at any time.
                                            All payments are processed securely and your card information is never stored on our servers.
                                        </p>
                                        <p className="mt-2 font-medium text-red-700">
                                            <strong>Refund Policy:</strong> No refunds are issued for canceled subscriptions.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <ExclamationCircleIcon className="h-5 w-5 text-blue-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800">API Key Requirements</h3>
                                    <div className="mt-2 text-sm text-blue-700">
                                        <p>
                                            <strong>Important:</strong> PostMgr requires you to provide your own OpenAI and Gemini API keys
                                            for AI content generation. This subscription covers access to our platform features only.
                                        </p>
                                        <p className="mt-1">
                                            AI usage costs are billed directly by OpenAI and Google to your API accounts.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default Billing;